/**
 * Shared 3-column pagination footer used by the client-paginated report pages
 * (Daily Attendance Logs, Random Attendance Logs, Mood Report). These pages all
 * page in memory over a fully-fetched dataset, so they know the total row count
 * and render "Showing X–Y of Z" plus "Page N of M".
 *
 * Layout: rows-per-page on the left, page nav in the true center (matching
 * flex-1 spacers keep it centered even when the left block widens), and the
 * right column intentionally empty so nothing falls under the fixed SSI /
 * "New custom report" widget at bottom-right.
 *
 * NOTE: the server-paginated Grooming report intentionally does NOT use this
 * component — it has no total count, so it renders a structurally different
 * footer ("Showing X–Y" with no total, "Page N" with no "of M", and a Next
 * button that carries a title attribute). Do not fold it in here.
 */

interface PageSizeOption {
  label: string;
  value: number;
}

interface PaginationFooterProps {
  /** Current 1-based page, already clamped to [1, totalPages]. */
  safePage: number;
  /** Total number of pages. */
  totalPages: number;
  /** Current rows-per-page value. */
  pageSize: number;
  /** Options for the rows-per-page select. */
  pageSizeOptions: PageSizeOption[];
  /** Total number of rows across all pages (used in the "of Z" range text). */
  totalRows: number;
  /** Called with the new rows-per-page value when the select changes. */
  onPageSizeChange: (value: number) => void;
  /** Called when the Previous button is clicked. */
  onPrevPage: () => void;
  /** Called when the Next button is clicked. */
  onNextPage: () => void;
}

export default function PaginationFooter({
  safePage,
  totalPages,
  pageSize,
  pageSizeOptions,
  totalRows,
  onPageSizeChange,
  onPrevPage,
  onNextPage,
}: PaginationFooterProps) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 px-1">
      <div className="flex items-center gap-3 flex-1 min-w-[200px]">
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 bg-white"
          title="Rows per page"
        >
          {pageSizeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <span className="text-sm text-gray-600">
          {(() => {
            const startRow = (safePage - 1) * pageSize + 1;
            const endRow = Math.min(safePage * pageSize, totalRows);
            return `Showing ${startRow}–${endRow} of ${totalRows}`;
          })()}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onPrevPage}
          disabled={safePage === 1}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <span className="text-sm text-gray-700 px-2">Page {safePage} of {totalPages}</span>
        <button
          onClick={onNextPage}
          disabled={safePage >= totalPages}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
      <div className="flex-1" aria-hidden="true" />
    </div>
  );
}
